import { extractCountryCodeFromJurisdictionCountryCode } from '@dealstack/frontend/app/pages/Project/Cap/CapEntityManageFlow/common/utils.ts';
import { useTranslationCapManage } from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks.tsx';
import { defaultTheme } from '@dealstack/frontend/shared/mechanism/styleguide/defaultTheme.ts';
import { Checkbox, Flex, Text, Tooltip } from '@mantine/core';
import { memo, useMemo } from 'react';
import ReactCountryFlag from 'react-country-flag';
import { Icon } from '@dealstack/frontend/shared/components/fundament/Icon/Icon.tsx';
import { isPersonLegal } from '@dealstack/shared/lib/common/business/person.ts';
import { GetPersonForCapRes } from '@dealstack/shared/lib/generated';
import { EntityTags } from '@dealstack/frontend/app/pages/Project/Cap/common/EntityTags/EntityTags';
import {
	PersonCardStyledPin,
	StyledPersonCard,
	StyledProfileIcon,
} from '@dealstack/frontend/app/pages/Project/Cap/CapEntityManageFlow/CapManageSidebar/PersonsList/components.ts';
import { TEST_ELEMENT_IDS, makeComplexDataTestId } from '@dealstack/shared/tests/testElementIds';

interface Props {
	person: GetPersonForCapRes;
	isSelected: boolean;
	onCardClick: (personId: string) => void;
	showWarning?: boolean;
	warningMessage?: string;
	isPinned: boolean;
	onPinValueChange: (personId: string, isPinned: boolean) => void;
	showCheckbox: boolean;
	personCheckboxValue: boolean;
	onPersonCheckboxChange: (personId: string, isChecked: boolean) => void;
	hidePin?: boolean;
}

const PersonCard = ({
	person,
	isSelected,
	showCheckbox,
	personCheckboxValue,
	onPersonCheckboxChange,
	onCardClick,
	isPinned,
	onPinValueChange,
	showWarning,
	warningMessage,
	hidePin,
}: Props) => {
	const tCapManage = useTranslationCapManage();

	const countryCode = useMemo(
		() => extractCountryCodeFromJurisdictionCountryCode(person.jurisdictionCountryCode),
		[person.jurisdictionCountryCode]
	);

	const description = isPersonLegal(person.type) ? (
		tCapManage('sidebar.entity_card.description', {
			regNumber: person.corporateRegistrationNumber ?? '',
		})
	) : (
		<StyledProfileIcon>
			<Icon variant="user-profile" size="md" />
		</StyledProfileIcon>
	);

	return (
		<Flex gap="0.25rem">
			{showCheckbox && (
				<Checkbox
					sx={{ padding: '0.25rem' }}
					checked={personCheckboxValue}
					onChange={(e) => {
						e.stopPropagation();
						onPersonCheckboxChange(person.id, !personCheckboxValue);
					}}
				/>
			)}
			<StyledPersonCard
				data-testid={makeComplexDataTestId(
					TEST_ELEMENT_IDS.CAP_DEAL_DIRECTORY_PERSON_NAME,
					person.computedName
				)}
				$isSelected={isSelected}
				onClick={() => onCardClick(person.id)}
			>
				<Flex justify="space-between">
					<Flex direction="column" justify="space-between" gap="0.5rem" maw="14rem">
						<Flex direction="column" align="start">
							<Text fw={600} style={{ wordBreak: 'break-word' }} c="dark.8">
								{person.computedName}
							</Text>
							{typeof description === 'string' ? (
								<Text
									truncate
									lineClamp={1}
									style={{ wordBreak: 'break-all' }}
									title={description}
									c="dark.8"
								>
									{description}
								</Text>
							) : (
								description
							)}
						</Flex>
						<EntityTags
							tags={person.tags}
							visibleTagCount={1}
							dropdownPosition="bottom-start"
						/>
					</Flex>
					<Flex direction="column" justify="space-between" align="flex-end" gap="0.5rem">
						{showWarning && (
							<Tooltip withinPortal label={warningMessage}>
								<Flex>
									<Icon
										variant="information-filled"
										style={{ color: defaultTheme.v2.color.warning[800] }}
										size="lg"
									/>
								</Flex>
							</Tooltip>
						)}
						<Flex>
							{countryCode && (
								<ReactCountryFlag
									countryCode={countryCode}
									svg
									style={{
										width: '1em',
										height: '1em',
									}}
									title={countryCode}
								/>
							)}
						</Flex>

						{!hidePin && (
							<PersonCardStyledPin
								$isActive={isPinned}
								onClick={(e) => {
									e.stopPropagation();
									onPinValueChange(person.id, !isPinned);
								}}
								data-testid={makeComplexDataTestId(
									TEST_ELEMENT_IDS.CAP_SIDEBAR_PERSON_PIN_BUTTON,
									person.computedName
								)}
							>
								<Icon variant="pin" size="lg" />
							</PersonCardStyledPin>
						)}
					</Flex>
				</Flex>
			</StyledPersonCard>
		</Flex>
	);
};

const Memoized = memo(PersonCard);

export { Memoized as PersonCard };
